import type { AxiosInstance } from 'axios'
import { useCallback } from 'react'
import { useApiContext } from './api'
import type { Attachment } from './types'

export interface IRDocumentSource {
  sourceType?: 'disclosure' | null
  sourceDocumentId?: string | null
}

export interface IRResultVideoConfig {
  mode: 'upload' | 'url' | null
  attachmentId?: string | null
  fileName?: string | null
  fileType?: string | null
  url?: string | null
  publishedUrl?: string | null
}

export interface IRResultQAPair {
  id: string
  question: string
  answer: string
}

export interface IRResultsExtras {
  video: IRResultVideoConfig
  qa: IRResultQAPair[]
}

export interface IRFundraisingOfferStatistic {
  id: string
  label: string
  value: string
}

export const defaultFundraisingOfferStatistics =
  (): IRFundraisingOfferStatistic[] => [
    { id: 'targetInvestment', label: 'Target Investment', value: '' },
    {
      id: 'offerPricePerShare',
      label: 'Offer Price per share',
      value: '',
    },
    {
      id: 'discountToSharePrice',
      label: 'Discount to share price',
      value: '',
    },
    {
      id: 'newOrdinarySharesToBeIssued',
      label: 'New Ordinary Shares to be issued',
      value: '',
    },
    {
      id: 'enlargedShareCapitalPercentage',
      label: '% of enlarged share capital',
      value: '',
    },
  ]

export interface IRFundraisingConfig {
  enabled: boolean
  generalInfo: {
    offerStatistics?: IRFundraisingOfferStatistic[]
    targetInvestment?: string
    offerPricePerShare?: string
    discountToSharePrice?: string
    newOrdinarySharesToBeIssued?: string
    enlargedShareCapitalPercentage?: string
    offerOpenFrom: string
    offerClosing: string
  }
  presentation: {
    documentIds: string[]
    fileLabels?: Record<string, string[]>
    documentLabels?: string[]
    badgeAttachmentId?: string | null
    badgeFileName?: string | null
    managementTeamImageAttachmentId?: string | null
    managementTeamImageFileName?: string | null
    badgeUrl?: string | null
    managementTeamImageUrl?: string | null
  }
  terms: {
    documentIds: string[]
    fileLabels?: Record<string, string[]>
    documentLabels?: string[]
  }
  documents: {
    documentIds: string[]
    fileLabels?: Record<string, string[]>
    documentLabels?: string[]
  }
  qa: IRResultQAPair[]
}

export const getFundraisingOfferStatistics = (
  generalInfo: IRFundraisingConfig['generalInfo']
): IRFundraisingOfferStatistic[] => {
  if (Array.isArray(generalInfo.offerStatistics)) {
    return generalInfo.offerStatistics
      .map((statistic) => ({
        id: statistic.id,
        label: statistic.label.trim(),
        value: statistic.value,
      }))
      .filter((statistic) => statistic.id && statistic.label)
  }

  return defaultFundraisingOfferStatistics().map((statistic) => ({
    ...statistic,
    value: getLegacyFundraisingOfferStatisticValue(generalInfo, statistic.id),
  }))
}

const getLegacyFundraisingOfferStatisticValue = (
  generalInfo: IRFundraisingConfig['generalInfo'],
  key: string
): string => {
  if (
    key === 'targetInvestment' ||
    key === 'offerPricePerShare' ||
    key === 'discountToSharePrice' ||
    key === 'newOrdinarySharesToBeIssued' ||
    key === 'enlargedShareCapitalPercentage'
  ) {
    return generalInfo[key] || ''
  }
  return ''
}

interface IRResultsQAOrderRequest {
  qaIds: string[]
}

export interface IRCompanyConfig {
  companyName: string
  logoUrl: string | null
  companyWebsite: string
  companyDescription: string
  companyLinkedinUrl?: string
  companyYoutubeUrl?: string
  companyXUrl?: string
  companyFacebookUrl?: string
  companyInstagramUrl?: string
  irContactName?: string
  irContactEmail: string
  companyStockTicker: string
  companyStockExchange: string
  sharePriceLink: string
}

export interface IRThemeConfig {
  title: string
  subtitle: string
  primaryColor: string
  textColor: string
  titleAndSubtitleColor: string
  fontStyle: string
  heroImageUrl: string | null
  heroImageUrls?: string[]
  heroCarouselSpeedSeconds?: number
}

export interface IRDocumentConfig {
  corporate: {
    selected: string[]
    fileLabels: Record<string, string[]>
    fileNames: Record<string, string | null>
    fileSources?: Record<string, IRDocumentSource>
    documentLabels: string[]
  }
  regulatory: {
    selected: string[]
    fileLabels: Record<string, string[]>
    fileNames: Record<string, string | null>
    fileSources?: Record<string, IRDocumentSource>
    documentLabels: string[]
  }
  reports: {
    selected: string[]
    fileLabels: Record<string, string[]>
    fileNames: Record<string, string | null>
    fileSources?: Record<string, IRDocumentSource>
    documentLabels: string[]
    resultsExtras?: IRResultsExtras
  }
  documents: {
    selected: string[]
    fileLabels: Record<string, string[]>
    fileNames: Record<string, string | null>
    fileSources?: Record<string, IRDocumentSource>
    documentLabels: string[]
  }
}

export interface IRDocument {
  attachmentId: string
  fileName: string
  fileType: string
  fileSize: number
  createdAt: string
  updatedAt: string
  labels: string[]
  publishedDate: string
  isPreview?: boolean
  category?: string
  subheading?: string
  duration?: number
  thumbnailUrl?: string
  publicUrl?: string
  attachmentType: string
  fileId?: string
}

export interface IRNewsletterConfig {
  lastSentAt?: string | null
  lastSentByUserId?: string | null
  lastSentFingerprint?: string | null
  lastSentSnapshot?: {
    companySlugPublished?: string | null
    company?: Partial<IRCompanyConfig>
    documents?: Partial<
      Record<
        'corporate' | 'regulatory' | 'reports' | 'documents',
        Array<{
          fileId: string
          fileName: string
          labels: string[]
          publishedDate: string
          sourceType?: 'disclosure' | null
          sourceDocumentId?: string | null
        }>
      >
    >
    fundraising?: Partial<IRFundraisingConfig>
  } | null
  lastRecipientCount?: number | null
  lastSubject?: string | null
}

export interface IRConfig {
  companySlugPreview: string | null
  companySlugPublished: string | null
  publicSiteUrl?: string | null
  companyConfigPreview: IRCompanyConfig
  companyConfigPublished: IRCompanyConfig
  themeConfigPreview: IRThemeConfig
  themeConfigPublished: IRThemeConfig
  documentConfigPreview: {
    corporate: Array<{
      fileId: string
      labels: string[]
      fileName: string | null
      publishedDate?: string | null
      sourceType?: 'disclosure' | null
      sourceDocumentId?: string | null
    }>
    regulatory: Array<{
      fileId: string
      labels: string[]
      fileName: string | null
      publishedDate?: string | null
      sourceType?: 'disclosure' | null
      sourceDocumentId?: string | null
    }>
    reports: {
      documents: Array<{
        fileId: string
        labels: string[]
        fileName: string | null
        publishedDate?: string | null
        sourceType?: 'disclosure' | null
        sourceDocumentId?: string | null
      }>
      resultsExtras?: IRResultsExtras
    }
    documents: Array<{
      fileId: string
      labels: string[]
      fileName: string | null
      publishedDate?: string | null
      sourceType?: 'disclosure' | null
      sourceDocumentId?: string | null
    }>
  }
  documentConfigPublished: {
    corporate: Array<{
      fileId: string
      labels: string[]
      fileName: string | null
      publishedDate: string
      sourceType?: 'disclosure' | null
      sourceDocumentId?: string | null
    }>
    regulatory: Array<{
      fileId: string
      labels: string[]
      fileName: string | null
      publishedDate: string
      sourceType?: 'disclosure' | null
      sourceDocumentId?: string | null
    }>
    reports: {
      documents: Array<{
        fileId: string
        labels: string[]
        fileName: string | null
        publishedDate: string
        sourceType?: 'disclosure' | null
        sourceDocumentId?: string | null
      }>
      resultsExtras?: IRResultsExtras
    }
    documents: Array<{
      fileId: string
      labels: string[]
      fileName: string | null
      publishedDate: string
      sourceType?: 'disclosure' | null
      sourceDocumentId?: string | null
    }>
  }
  fundraisingConfigPreview?: IRFundraisingConfig
  fundraisingConfigPublished?: IRFundraisingConfig
  newsletterConfig?: IRNewsletterConfig
  scheduledPublishes?: IRDocumentScheduledPublish[]
}

export interface InvestorRelationsConfig {
  companySlugPreview: string | null
  companySlugPublished: string | null
  companyConfig: IRCompanyConfig
  themeConfig: IRThemeConfig
  documentConfig: {
    corporate: Array<{
      fileId: string
      labels: string[]
      fileName: string | null
      publishedDate?: string | null
      sourceType?: 'disclosure' | null
      sourceDocumentId?: string | null
    }>
    regulatory: Array<{
      fileId: string
      labels: string[]
      fileName: string | null
      publishedDate?: string | null
      sourceType?: 'disclosure' | null
      sourceDocumentId?: string | null
    }>
    reports: {
      documents: Array<{
        fileId: string
        labels: string[]
        fileName: string | null
        publishedDate?: string | null
        sourceType?: 'disclosure' | null
        sourceDocumentId?: string | null
      }>
      resultsExtras?: IRResultsExtras
    }
    documents: Array<{
      fileId: string
      labels: string[]
      fileName: string | null
      publishedDate?: string | null
      sourceType?: 'disclosure' | null
      sourceDocumentId?: string | null
    }>
  }
  fundraisingConfig?: IRFundraisingConfig
}

export interface IRNewsletterChange {
  type: string
  label: string
  description: string
}

export interface IRNewsletterDraft {
  subject: string
  documentTitle?: string
  message: string
  messageHtml?: string
  ctaText?: string
  targetUrl?: string
  targetSection?: string
  targetDocumentId?: string
  recipientIds?: string[]
}

export interface IRNewsletterPreviewRequest extends IRNewsletterDraft {
  companySocialLinks?: {
    linkedin?: string
    youtube?: string
    x?: string
    facebook?: string
    instagram?: string
  }
}

export interface IRNewsletterRecipient {
  id: string
  name: string | null
  email: string | null
  firm: string | null
  investorType: string | null
  source: string | null
  sourceLabel: string | null
  subscriptionStatus?: 'subscribed' | 'unsubscribed' | string | null
  isSubscribedToNewsletter?: boolean
  hasReceivedSelectedNewsletter?: boolean
  lastNewsletterSentAt?: string | null
  lastNewsletterSendId?: string | null
}

export interface IRNewsletterScheduledSend {
  id: string
  organisationId: string
  scheduledFor: string
  status: 'scheduled' | 'cancelled' | 'queued' | 'failed'
  subject: string
  documentTitle?: string | null
  recipientCount: number
  newsletterSendId?: string | null
  taskId?: string | null
  failureReason?: string | null
  createdByUserId: string
  cancelledByUserId?: string | null
  createdAt?: string | null
  cancelledAt?: string | null
  queuedAt?: string | null
  documentScheduledPublishId?: string | null
}

export interface IRDocumentScheduledPublishDocument {
  fileId: string
  fileName?: string | null
  labels?: string[]
  publishedDate?: string | null
  sourceType?: 'disclosure' | null
  sourceDocumentId?: string | null
}

export interface IRDocumentScheduledPublish {
  id: string
  organisationId: string
  scheduledFor: string
  status: 'scheduled' | 'cancelled' | 'published' | 'failed'
  section: 'corporate' | 'regulatory' | 'reports' | 'documents'
  documents: IRDocumentScheduledPublishDocument[]
  documentCount: number
  newsletterScheduledSend?: IRNewsletterScheduledSend | null
  failureReason?: string | null
  createdByUserId: string
  cancelledByUserId?: string | null
  createdAt?: string | null
  cancelledAt?: string | null
  publishedAt?: string | null
}

export interface IRNewsletterStatus {
  canSend: boolean
  disabledReason: string | null
  recipientCount: number
  recipients: IRNewsletterRecipient[]
  otherRecipientCount?: number
  otherRecipients?: IRNewsletterRecipient[]
  scheduledSends?: IRNewsletterScheduledSend[]
  changes: IRNewsletterChange[]
  draft: IRNewsletterDraft
  lastSentAt: string | null
  lastSentByUserId?: string | null
  lastRecipientCount: number | null
  lastSubject?: string | null
  lastMessageHtml?: string | null
  publicUrl: string | null
}

export type IRNewsletterSendStatus =
  | 'queued'
  | 'sending'
  | 'completed'
  | 'partial_failed'
  | 'failed'

export interface IRNewsletterFailure {
  email: string
  reason: string
}

export interface IRNewsletterSendProgress {
  type?: 'newsletter_send_progress'
  newsletterSendId: string
  status: IRNewsletterSendStatus
  sentCount: number
  failedCount: number
  totalCount: number
  pendingCount?: number
  failures?: IRNewsletterFailure[]
}

export interface IRNewsletterSendResponse {
  taskId: string
  newsletterSendId: string
  status: 'started'
  recipientCount: number
  changes: IRNewsletterChange[]
}

export interface IRNewsletterPreviewResponse {
  subject: string
  html: string
}

export interface IRNewsletterScheduleRequest extends IRNewsletterDraft {
  scheduledFor: string
}

export interface IRNewsletterScheduleResponse {
  status: 'scheduled'
  scheduledSend: IRNewsletterScheduledSend
  changes: IRNewsletterChange[]
}

export interface IRNewsletterScheduleCancelResponse {
  status: 'cancelled'
  scheduledSend: IRNewsletterScheduledSend
}

export interface IRDocumentPublishScheduleRequest {
  scheduledFor: string
  section: 'corporate' | 'regulatory' | 'reports' | 'documents'
  documents: IRDocumentScheduledPublishDocument[]
  newsletter?: IRNewsletterDraft | null
}

export interface IRDocumentPublishScheduleResponse {
  status: 'scheduled'
  scheduledPublish: IRDocumentScheduledPublish
  changes: IRNewsletterChange[]
}

export interface IRDocumentPublishScheduleCancelResponse {
  status: 'cancelled'
  scheduledPublish: IRDocumentScheduledPublish
}

export interface IRApprovedDisclosureDocument {
  id: string
  title: string
  status: 'approved'
  updatedAt?: string | null
  editorType?: 'tiptap' | 'onlyoffice'
}

export interface InvestorRelationsConfigContextType {
  config: InvestorRelationsConfig | null
  loading: boolean
  error: Error | null
}

export const useInvestorRelationsAPI = () => {
  const { api } = useApiContext()

  const getIRConfig = useCallback(
    async (organisationId: string): Promise<IRConfig> => {
      const response = await api.get(
        `/admin/investor-relations/${organisationId}/config`
      )
      return response.data
    },
    [api]
  )

  const saveIRConfig = async (
    organisationId: string,
    config: {
      companySlugPreview?: string
      companyConfig?: IRCompanyConfig
      themeConfig?: IRThemeConfig
      documentConfig?: IRDocumentConfig
      fundraisingConfig?: IRFundraisingConfig
    }
  ): Promise<IRConfig> => {
    const response = await api.put(
      `/admin/investor-relations/${organisationId}/config`,
      config
    )
    return response.data
  }

  const publishIRConfig = async (organisationId: string): Promise<IRConfig> => {
    const response = await api.post(
      `/admin/investor-relations/${organisationId}/config/publish`
    )
    return response.data
  }

  const publishFundraisingCampaign = async (
    organisationId: string,
    enabled: boolean
  ): Promise<IRConfig> => {
    const response = await api.post(
      `/admin/investor-relations/${organisationId}/fundraising/publish`,
      { enabled }
    )
    return response.data
  }

  const getIRNewsletterStatus = async (
    organisationId: string,
    params?: {
      targetSection?: string | null
      targetDocumentId?: string | null
    }
  ): Promise<IRNewsletterStatus> => {
    const response = await api.get(
      `/admin/investor-relations/${organisationId}/newsletter/status`,
      {
        params: {
          targetSection: params?.targetSection || undefined,
          targetDocumentId: params?.targetDocumentId || undefined,
        },
      }
    )
    return response.data
  }

  const sendIRNewsletter = async (
    organisationId: string,
    payload: IRNewsletterDraft
  ): Promise<IRNewsletterSendResponse> => {
    const response = await api.post(
      `/admin/investor-relations/${organisationId}/newsletter/send`,
      payload
    )
    return response.data
  }

  const previewIRNewsletterEmail = async (
    organisationId: string,
    payload: IRNewsletterPreviewRequest
  ): Promise<IRNewsletterPreviewResponse> => {
    const response = await api.post(
      `/admin/investor-relations/${organisationId}/newsletter/email-preview`,
      payload
    )
    return response.data
  }

  const scheduleIRNewsletter = async (
    organisationId: string,
    payload: IRNewsletterScheduleRequest
  ): Promise<IRNewsletterScheduleResponse> => {
    const response = await api.post(
      `/admin/investor-relations/${organisationId}/newsletter/schedules`,
      payload
    )
    return response.data
  }

  const cancelScheduledIRNewsletter = async (
    organisationId: string,
    scheduledSendId: string
  ): Promise<IRNewsletterScheduleCancelResponse> => {
    const response = await api.delete(
      `/admin/investor-relations/${organisationId}/newsletter/schedules/${scheduledSendId}`
    )
    return response.data
  }

  const scheduleIRDocumentPublish = async (
    organisationId: string,
    payload: IRDocumentPublishScheduleRequest
  ): Promise<IRDocumentPublishScheduleResponse> => {
    const response = await api.post(
      `/admin/investor-relations/${organisationId}/documents/schedules`,
      payload
    )
    return response.data
  }

  const cancelScheduledIRDocumentPublish = async (
    organisationId: string,
    scheduledPublishId: string
  ): Promise<IRDocumentPublishScheduleCancelResponse> => {
    const response = await api.delete(
      `/admin/investor-relations/${organisationId}/documents/schedules/${scheduledPublishId}`
    )
    return response.data
  }

  const uploadIRDocument = async (
    organisationId: string,
    files: File[],
    section:
      | 'corporate'
      | 'regulatory'
      | 'reports'
      | 'documents'
      | 'fundraising-presentation'
      | 'fundraising-terms'
      | 'fundraising-documents'
      | 'fundraising-badge'
      | 'fundraising-management-team'
  ): Promise<{ attachments: Attachment[] }> => {
    const formData = new FormData()
    for (const file of files) {
      formData.append('files', file)
    }
    formData.append('section', section)

    const response = await api.post(
      `/admin/investor-relations/${organisationId}/documents/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  const copyDocumentToIRBucket = async (
    organisationId: string,
    attachmentId: string,
    section:
      | 'corporate'
      | 'regulatory'
      | 'reports'
      | 'documents'
      | 'fundraising-presentation'
      | 'fundraising-terms'
      | 'fundraising-documents'
      | 'fundraising-badge'
      | 'fundraising-management-team'
  ): Promise<{ detail: string; attachments: Attachment[] }> => {
    const formData = new FormData()
    formData.append('attachment_id', attachmentId)
    formData.append('section', section)

    const response = await api.post(
      `/admin/investor-relations/${organisationId}/documents/copy-to-ir`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  const getApprovedDisclosureDocuments = async (
    organisationId: string
  ): Promise<{ documents: IRApprovedDisclosureDocument[] }> => {
    const response = await api.get(
      `/admin/investor-relations/${organisationId}/documents/disclosure/approved`
    )
    return response.data
  }

  const importDisclosureDocumentToIR = async (
    organisationId: string,
    disclosureDocumentId: string,
    section: 'corporate' | 'regulatory' | 'reports' | 'documents'
  ): Promise<{
    detail: string
    attachment: Attachment
    sourceType: 'disclosure'
    sourceDocumentId: string
  }> => {
    /**
     * Import an approved disclosure document into an IR section.
     *
     * The backend renders the disclosure document, converts it to a PDF,
     * uploads that PDF into the IR bucket, and returns the new IR attachment
     * plus metadata linking it back to the source disclosure document.
     */
    const formData = new FormData()
    formData.append('disclosure_document_id', disclosureDocumentId)
    formData.append('section', section)

    const response = await api.post(
      `/admin/investor-relations/${organisationId}/documents/import-disclosure`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  const getIRDocuments = async (
    organisationId: string,
    section:
      | 'corporate'
      | 'regulatory'
      | 'reports'
      | 'documents'
      | 'fundraising-presentation'
      | 'fundraising-terms'
      | 'fundraising-documents'
      | 'fundraising-badge'
      | 'fundraising-management-team',
    includePreview = false
  ): Promise<{ documents: IRDocument[] }> => {
    const response = await api.get(
      `/admin/investor-relations/${organisationId}/documents/${section}`,
      {
        params: { include_preview: includePreview },
      }
    )
    return response.data
  }

  const uploadIRLogo = async (
    organisationId: string,
    imageBlob: Blob,
    filename = 'logo.png'
  ): Promise<{ logoUrl: string }> => {
    // Convert blob to File
    const file = new File([imageBlob], filename, { type: imageBlob.type })

    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post(
      `/admin/investor-relations/${organisationId}/logo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  const uploadIRHeroImage = async (
    organisationId: string,
    imageBlob: Blob,
    filename = 'hero.png'
  ): Promise<{ heroImageUrl: string }> => {
    // Convert blob to File
    const file = new File([imageBlob], filename, { type: imageBlob.type })

    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post(
      `/admin/investor-relations/${organisationId}/hero-image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  const removeIRDocument = async (
    organisationId: string,
    section:
      | 'corporate'
      | 'regulatory'
      | 'reports'
      | 'documents'
      | 'fundraising-presentation'
      | 'fundraising-terms'
      | 'fundraising-documents'
      | 'fundraising-badge'
      | 'fundraising-management-team',
    fileId: string
  ): Promise<IRConfig> => {
    const response = await api.delete(
      `/admin/investor-relations/${organisationId}/documents/${section}/${fileId}`
    )
    return response.data
  }

  const unpublishIRConfig = async (
    organisationId: string
  ): Promise<IRConfig> => {
    const response = await api.post(
      `/admin/investor-relations/${organisationId}/config/unpublish`
    )
    return response.data
  }

  const getIRLogo = async (
    organisationId: string
  ): Promise<{ logoUrl: string }> => {
    const response = await api.get(
      `/admin/investor-relations/${organisationId}/logo`
    )
    return response.data
  }

  const getIRHeroImage = async (
    organisationId: string
  ): Promise<{ heroImageUrl: string }> => {
    const response = await api.get(
      `/admin/investor-relations/${organisationId}/hero-image`
    )
    return response.data
  }

  const uploadIRResultsVideo = async (
    organisationId: string,
    file: File
  ): Promise<{ resultsExtras: IRResultsExtras }> => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post(
      `/admin/investor-relations/${organisationId}/results/video-upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  const setIRResultsVideoLink = async (
    organisationId: string,
    url: string
  ): Promise<{ resultsExtras: IRResultsExtras }> => {
    const response = await api.put(
      `/admin/investor-relations/${organisationId}/results/video-link`,
      { url }
    )
    return response.data
  }

  const removeIRResultsVideo = async (
    organisationId: string
  ): Promise<{ resultsExtras: IRResultsExtras }> => {
    const response = await api.delete(
      `/admin/investor-relations/${organisationId}/results/video`
    )
    return response.data
  }

  const createIRResultsQA = async (
    organisationId: string,
    payload: { question: string; answer: string }
  ): Promise<{ resultsExtras: IRResultsExtras }> => {
    const response = await api.post(
      `/admin/investor-relations/${organisationId}/results/qa`,
      payload
    )
    return response.data
  }

  const updateIRResultsQA = async (
    organisationId: string,
    qaId: string,
    payload: { question: string; answer: string }
  ): Promise<{ resultsExtras: IRResultsExtras }> => {
    const response = await api.put(
      `/admin/investor-relations/${organisationId}/results/qa/${qaId}`,
      payload
    )
    return response.data
  }

  const deleteIRResultsQA = async (
    organisationId: string,
    qaId: string
  ): Promise<{ resultsExtras: IRResultsExtras }> => {
    const response = await api.delete(
      `/admin/investor-relations/${organisationId}/results/qa/${qaId}`
    )
    return response.data
  }

  const reorderIRResultsQA = async (
    organisationId: string,
    qaIds: string[]
  ): Promise<{ resultsExtras: IRResultsExtras }> => {
    const payload: IRResultsQAOrderRequest = { qaIds }
    const response = await api.put(
      `/admin/investor-relations/${organisationId}/results/qa/order`,
      payload
    )
    return response.data
  }

  return {
    getIRConfig,
    saveIRConfig,
    publishIRConfig,
    publishFundraisingCampaign,
    getIRNewsletterStatus,
    sendIRNewsletter,
    previewIRNewsletterEmail,
    scheduleIRNewsletter,
    cancelScheduledIRNewsletter,
    scheduleIRDocumentPublish,
    cancelScheduledIRDocumentPublish,
    uploadIRDocument,
    copyDocumentToIRBucket,
    getApprovedDisclosureDocuments,
    importDisclosureDocumentToIR,
    getIRDocuments,
    uploadIRLogo,
    uploadIRHeroImage,
    removeIRDocument,
    unpublishIRConfig,
    getIRLogo,
    getIRHeroImage,
    uploadIRResultsVideo,
    setIRResultsVideoLink,
    removeIRResultsVideo,
    createIRResultsQA,
    updateIRResultsQA,
    deleteIRResultsQA,
    reorderIRResultsQA,
  }
}
